if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"], .gigi-add-to-cart-button[type="submit"]');
        this.isGigiSubmitButton = this.submitButton?.classList.contains('gigi-add-to-cart-button');
        this.submitButtonText = this.isGigiSubmitButton
          ? this.submitButton.querySelector('.gigi-add-to-cart-button__ready')
          : this.submitButton?.querySelector('span');
        this.submitButtonLabel = this.isGigiSubmitButton
          ? this.submitButton.querySelector('[data-gigi-add-to-cart-label]')
          : this.submitButtonText;
        this.loadingSpinner = this.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer') && this.submitButton && !this.submitButton.getAttribute('aria-haspopup')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.shouldSubmitNatively) {
          this.submitNativeCartForm();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.isGigiSubmitButton) {
          this.submitButton.classList.add('is-loading');
        } else if (this.loadingSpinner) {
          this.loadingSpinner.classList.remove('hidden');
        }

        const formData = new FormData(this.form);
        const quantityState = this.getQuantityState(formData);
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
        let url = routes.cart_add_url;
        let config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (quantityState?.delta <= 0) {
          url = routes.cart_update_url;
          config = fetchConfig();
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify({
            updates: {
              [quantityState.variantId]: quantityState.requestedQuantity,
            },
            sections,
            sections_url: window.location.pathname,
          });
        } else {
          delete config.headers['Content-Type'];
          if (quantityState) formData.set('quantity', quantityState.delta);
        }

        if (this.cart) {
          if (!config.body) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement(document.activeElement);
        }
        if (!config.body) config.body = formData;

        fetch(url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            this.updateQuantityState(quantityState, response);
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.isGigiSubmitButton) {
              this.submitButton.classList.remove('is-loading');
            } else if (this.loadingSpinner) {
              this.loadingSpinner.classList.add('hidden');
            }
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonLabel) this.submitButtonLabel.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonLabel) {
            this.submitButtonLabel.textContent = this.submitButton.dataset.addToCartLabel || window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getQuantityState(formData) {
        const quantityInput = this.quantityInput;
        if (!quantityInput?.hasAttribute('data-cart-quantity')) return null;

        const variantId = formData.get('id')?.toString();
        let requestedQuantity = parseInt(quantityInput.value, 10);
        const cartQuantity = parseInt(quantityInput.dataset.cartQuantity || '0', 10);

        if (!variantId || Number.isNaN(requestedQuantity) || Number.isNaN(cartQuantity)) return null;

        if (quantityInput.hasAttribute('data-clamp-max')) {
          const maxQuantity = Number(quantityInput.dataset.max || quantityInput.max);
          if (Number.isFinite(maxQuantity) && requestedQuantity > maxQuantity) {
            requestedQuantity = maxQuantity;
            quantityInput.value = maxQuantity.toString();
          }
        }

        return {
          quantityInput,
          variantId,
          requestedQuantity: Math.max(requestedQuantity, 0),
          cartQuantity: Math.max(cartQuantity, 0),
          delta: requestedQuantity - cartQuantity,
        };
      }

      updateQuantityState(quantityState, response) {
        if (!quantityState) return;

        let updatedQuantity = quantityState.requestedQuantity;
        if (Array.isArray(response.items)) {
          const updatedItem = response.items.find((item) => item.variant_id?.toString() === quantityState.variantId);
          updatedQuantity = updatedItem ? updatedItem.quantity : 0;
        } else if (response.quantity !== undefined) {
          updatedQuantity = response.quantity;
        }

        quantityState.quantityInput.dataset.cartQuantity = updatedQuantity.toString();
      }

      get quantityInput() {
        const quantityInput = this.form.elements.quantity;
        return quantityInput instanceof HTMLInputElement ? quantityInput : null;
      }

      get shouldSubmitNatively() {
        return (
          !this.cart &&
          ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname) &&
          window.shopPermanentUrl &&
          this.form
        );
      }

      submitNativeCartForm() {
        const currentAction = this.form.getAttribute('action') || window.routes.cart_add_url;
        const cartAddPath = currentAction.startsWith('http') ? new URL(currentAction).pathname : currentAction;

        this.form.action = `${window.shopPermanentUrl}${cartAddPath}`;

        if (!this.form.querySelector('[name="return_to"]')) {
          const returnToInput = document.createElement('input');
          returnToInput.type = 'hidden';
          returnToInput.name = 'return_to';
          returnToInput.value = window.routes.cart_url || '/cart';
          this.form.appendChild(returnToInput);
        }

        HTMLFormElement.prototype.submit.call(this.form);
      }
    }
  );
}
